import { type Card, type HandRank, evaluateBestHand, compareHands } from "../lib/pokerEngine"

export interface HandEvaluation {
  handName: string
  handRank: number
  cards: Card[]
}

const VALUE_MAP: Record<string, number> = {
  "2": 2, "3": 3, "4": 4, "5": 5, "6": 6, "7": 7, "8": 8,
  "9": 9, "10": 10, "J": 11, "Q": 12, "K": 13, "A": 14,
}

const getCombinations = (arr: Card[], k: number): Card[][] => {
  if (k === 0) return [[]]
  if (arr.length < k) return []
  const [first, ...rest] = arr
  const withFirst = getCombinations(rest, k - 1).map(combo => [first!, ...combo])
  const withoutFirst = getCombinations(rest, k)
  return [...withFirst, ...withoutFirst]
}

const evaluatePartialHand = (cards: Card[]): HandEvaluation => {
  if (cards.length === 0) return { handName: "No Cards", handRank: -1, cards: [] }
  if (cards.length === 1) return { handName: "High Card", handRank: 0, cards }

  const sorted = [...cards].sort(
    (a, b) => (VALUE_MAP[b.value] ?? 0) - (VALUE_MAP[a.value] ?? 0)
  )

  const counts = new Map<string, Card[]>()
  for (const c of sorted) {
    const existing = counts.get(c.value) || []
    existing.push(c)
    counts.set(c.value, existing)
  }

  const groups = Array.from(counts.entries()).sort(
    (a, b) => b[1].length - a[1].length || (VALUE_MAP[b[0]] ?? 0) - (VALUE_MAP[a[0]] ?? 0)
  )

  if (groups[0]![1].length === 4)
    return { handName: "Four of a Kind", handRank: 7, cards: sorted }
  if (groups[0]![1].length === 3 && groups.length >= 2 && groups[1]![1].length >= 2)
    return { handName: "Full House", handRank: 6, cards: sorted }
  if (groups[0]![1].length === 3)
    return { handName: "Three of a Kind", handRank: 3, cards: sorted }
  if (groups[0]![1].length === 2 && groups.length >= 2 && groups[1]![1].length === 2)
    return { handName: "Two Pair", handRank: 2, cards: sorted }
  if (groups[0]![1].length === 2)
    return { handName: "Pair", handRank: 1, cards: sorted }

  return { handName: "High Card", handRank: 0, cards: sorted }
}

export const evaluateHand = (holeCards: Card[], communityCards: Card[]): HandEvaluation => {
  const allCards = [...holeCards, ...communityCards]

  if (allCards.length < 5) {
    return evaluatePartialHand(allCards)
  }

  // 5+ cards: find best 5-card combination and track which cards form it
  const combos = getCombinations(allCards, 5)
  let bestRank: HandRank | null = null
  let bestCombo: Card[] = combos[0] || []

  for (const combo of combos) {
    // evaluateBestHand with exactly 5 cards evaluates just that combination
    const rank = evaluateBestHand(combo)
    if (!bestRank || compareHands(rank, bestRank) > 0) {
      bestRank = rank
      bestCombo = combo
    }
  }

  return {
    handName: bestRank?.name ?? "High Card",
    handRank: bestRank?.rank ?? 0,
    cards: bestCombo,
  }
}

export interface DrawResult {
  drawName: string
  outs: number
  probability: number
}

export const detectDraws = (holeCards: Card[], communityCards: Card[]): DrawResult[] => {
  if (communityCards.length >= 5 || holeCards.length < 2) return []

  const allCards = [...holeCards, ...communityCards]
  const remaining = 52 - allCards.length
  if (remaining <= 0) return []

  const draws: DrawResult[] = []
  const currentEval = evaluateHand(holeCards, communityCards)

  // Flush Draw: exactly 4 of same suit (9 outs)
  if (currentEval.handRank < 5) {
    const suitCounts = new Map<string, number>()
    for (const c of allCards) {
      suitCounts.set(c.suit, (suitCounts.get(c.suit) || 0) + 1)
    }
    for (const [, count] of suitCounts) {
      if (count === 4) {
        draws.push({ drawName: "Flush Draw", outs: 9, probability: 9 / remaining })
        break
      }
    }
  }

  // Straight Draws
  if (currentEval.handRank < 4) {
    const valSet = new Set(allCards.map(c => VALUE_MAP[c.value] ?? 0))
    let foundOESD = false

    // OESD: 4 consecutive values with both ends completable
    for (let low = 2; low <= 11; low++) {
      if (valSet.has(low) && valSet.has(low + 1) && valSet.has(low + 2) && valSet.has(low + 3)) {
        const canCompleteLow = low - 1 >= 2 || low === 2
        const canCompleteHigh = low + 4 <= 14
        if (canCompleteLow && canCompleteHigh) {
          draws.push({ drawName: "Open-Ended Straight Draw", outs: 8, probability: 8 / remaining })
          foundOESD = true
          break
        }
      }
    }

    // Gutshot / Double Gutshot: 4 of 5 in any straight window
    if (!foundOESD) {
      const gutshotOuts = new Set<number>()
      const windows: number[][] = [
        [14, 2, 3, 4, 5],
        [2, 3, 4, 5, 6], [3, 4, 5, 6, 7], [4, 5, 6, 7, 8], [5, 6, 7, 8, 9],
        [6, 7, 8, 9, 10], [7, 8, 9, 10, 11], [8, 9, 10, 11, 12],
        [9, 10, 11, 12, 13], [10, 11, 12, 13, 14],
      ]
      for (const w of windows) {
        const present = w.filter(v => valSet.has(v))
        const missing = w.filter(v => !valSet.has(v))
        if (present.length === 4 && missing.length === 1) {
          gutshotOuts.add(missing[0]!)
        }
      }
      if (gutshotOuts.size > 0) {
        const outs = gutshotOuts.size * 4
        const name = gutshotOuts.size >= 2 ? "Open-Ended Straight Draw" : "Gutshot Straight Draw"
        draws.push({ drawName: name, outs, probability: outs / remaining })
      }
    }
  }

  // Set Potential: pocket pair that hasn't become trips (2 outs)
  if (currentEval.handRank < 3 && holeCards[0]!.value === holeCards[1]!.value) {
    const countInAll = allCards.filter(c => c.value === holeCards[0]!.value).length
    if (countInAll === 2) {
      draws.push({ drawName: "Set Potential", outs: 2, probability: 2 / remaining })
    }
  }

  // Two Pair Potential: exactly one pair, unpaired hole card could pair
  if (currentEval.handRank === 1 && holeCards[0]!.value !== holeCards[1]!.value) {
    let outs = 0
    for (const hc of holeCards) {
      const count = allCards.filter(c => c.value === hc.value).length
      if (count < 2) {
        outs += 4 - count
      }
    }
    if (outs > 0) {
      draws.push({ drawName: "Two Pair Potential", outs, probability: outs / remaining })
    }
  }

  return draws
}
